/* eslint-disable @typescript-eslint/no-explicit-any */
import { create } from 'zustand';
import type { Presentation, PresentationWithSlides, SlideGroup, Slide, SlideElement, SlideStyle } from '@proclaim/shared';
import { api } from '../shared/api';
import { useToastStore } from './toast-store';

interface PresentationState {
  presentations: Presentation[];
  currentPresentation: PresentationWithSlides | null;
  loading: boolean;
  error: string | null;

  fetchPresentations: (background?: boolean) => Promise<void>;
  fetchPresentation: (id: string, background?: boolean) => Promise<void>;
  createPresentation: (data: { name: string; category?: string }) => Promise<Presentation>;
  updatePresentation: (id: string, data: { name?: string; category?: string }) => Promise<void>;
  deletePresentation: (id: string) => Promise<void>;

  addSlide: (data: any) => Promise<Slide>;
  updateSlide: (id: string, data: any) => Promise<void>;
  deleteSlide: (id: string) => Promise<void>;
  duplicateSlide: (id: string) => Promise<void>;
  copySlidesFrom: (slideIds: string[], groupLabel?: string) => Promise<void>;
  reorderSlides: (slideIds: string[]) => Promise<void>;
  applyStyle: (slideIds: string[], style: SlideStyle, sourceElements?: SlideElement[]) => Promise<void>;

  addGroup: (data: { label: string }) => Promise<SlideGroup>;
  updateGroup: (id: string, data: { label?: string }) => Promise<void>;
  deleteGroup: (id: string) => Promise<void>;
}

const toast = () => useToastStore.getState();

export const usePresentationStore = create<PresentationState>((set, get) => ({
  presentations: [],
  currentPresentation: null,
  loading: false,
  error: null,

  fetchPresentations: async (background = false) => {
    if (!background) set({ loading: true, error: null });
    try {
      const presentations = await api.getPresentations();
      set({ presentations, loading: false });
    } catch (e: any) {
      set({ loading: false });
      if (!background) {
        set({ error: e.message });
        toast().show('error', 'No se pudieron cargar las presentaciones');
      }
    }
  },

  fetchPresentation: async (id: string, background = false) => {
    if (!background) set({ loading: true, error: null });
    try {
      const presentation = await api.getPresentation(id);
      set({ currentPresentation: presentation, loading: false });
    } catch (e: any) {
      set({ loading: false });
      if ((e as any)?.status === 404) {
        set({ currentPresentation: null });
        return;
      }
      if (!background) {
        set({ error: e.message });
        toast().show('error', 'No se pudo cargar la presentación');
      }
    }
  },

  createPresentation: async (data) => {
    try {
      const presentation = await api.createPresentation(data);
      set((s) => ({ presentations: [presentation, ...s.presentations] }));
      return presentation;
    } catch (e: any) {
      toast().show('error', 'No se pudo crear la presentación');
      throw e;
    }
  },

  updatePresentation: async (id, data) => {
    try {
      await api.updatePresentation(id, data);
      const { currentPresentation } = get();
      if (currentPresentation?.id === id) {
        set({ currentPresentation: { ...currentPresentation, ...data } as PresentationWithSlides });
      }
      get().fetchPresentations();
    } catch (e: any) {
      toast().show('error', 'No se pudo actualizar la presentación');
      throw e;
    }
  },

  deletePresentation: async (id) => {
    try {
      await api.deletePresentation(id);
      set((s) => ({ presentations: s.presentations.filter((p) => p.id !== id) }));
    } catch (e: any) {
      toast().show('error', 'No se pudo eliminar la presentación');
      throw e;
    }
  },

  addSlide: async (data) => {
    const { currentPresentation } = get();
    if (!currentPresentation) throw new Error('No presentation selected');
    try {
      const slide = await api.createSlide({ ...data, presentationId: currentPresentation.id });
      // Mirror server insert position: after the last slide of the same
      // section/group, otherwise append. Keeps group slides contiguous so the
      // live service order doesn't split the group into duplicate entries.
      const prev = get().currentPresentation ?? currentPresentation;
      const slides = prev.slides;
      let insertAfter = -1;
      if (slide.groupId) {
        for (let i = slides.length - 1; i >= 0; i--) {
          if (slide.sectionId ? slides[i].sectionId === slide.sectionId : slides[i].groupId === slide.groupId) {
            insertAfter = i;
            break;
          }
        }
        if (insertAfter === -1) {
          for (let i = slides.length - 1; i >= 0; i--) {
            if (slides[i].groupId === slide.groupId) { insertAfter = i; break; }
          }
        }
      }
      const next = insertAfter >= 0
        ? [...slides.slice(0, insertAfter + 1), slide, ...slides.slice(insertAfter + 1)]
        : [...slides, slide];
      set({ currentPresentation: { ...prev, slides: next } });
      return slide;
    } catch (e: any) {
      toast().show('error', 'No se pudo agregar el slide');
      throw e;
    }
  },

  updateSlide: async (id, data) => {
    try {
      await api.updateSlide(id, data);
      const { currentPresentation } = get();
      if (currentPresentation) {
        set({
          currentPresentation: {
            ...currentPresentation,
            slides: currentPresentation.slides.map((s) =>
              s.id === id
                ? { ...s, ...data, content: { ...s.content, ...(data.content ?? {}) }, style: { ...s.style, ...(data.style ?? {}) } }
                : s,
            ),
          },
        });
      }
    } catch (e: any) {
      toast().show('error', 'No se pudo guardar el slide');
      throw e;
    }
  },

  deleteSlide: async (id) => {
    try {
      await api.deleteSlide(id);
      const { currentPresentation } = get();
      if (currentPresentation) {
        set({
          currentPresentation: {
            ...currentPresentation,
            slides: currentPresentation.slides.filter((s) => s.id !== id),
          },
        });
      }
    } catch (e: any) {
      toast().show('error', 'No se pudo eliminar el slide');
      throw e;
    }
  },

  duplicateSlide: async (id) => {
    try {
      await api.duplicateSlide(id);
      const { currentPresentation } = get();
      if (currentPresentation) {
        get().fetchPresentation(currentPresentation.id);
      }
    } catch (e: any) {
      toast().show('error', 'No se pudo duplicar el slide');
      throw e;
    }
  },

  copySlidesFrom: async (slideIds, groupLabel) => {
    const { currentPresentation } = get();
    if (!currentPresentation) throw new Error('No presentation selected');
    try {
      await api.copySlides({ targetPresentationId: currentPresentation.id, slideIds, groupLabel });
      await get().fetchPresentation(currentPresentation.id, true);
      toast().show('success', `${slideIds.length} slide${slideIds.length !== 1 ? 's' : ''} copiado${slideIds.length !== 1 ? 's' : ''}`);
    } catch (e: any) {
      toast().show('error', 'No se pudieron copiar los slides');
      throw e;
    }
  },

  reorderSlides: async (slideIds) => {
    const { currentPresentation } = get();
    if (!currentPresentation) return;
    try {
      await api.reorderSlides(currentPresentation.id, slideIds);
      const slideMap = new Map(currentPresentation.slides.map((s) => [s.id, s]));
      const reordered = slideIds.map((id) => slideMap.get(id)!).filter(Boolean);
      set({ currentPresentation: { ...currentPresentation, slides: reordered } });
    } catch (e: any) {
      toast().show('error', 'No se pudo reordenar los slides');
      throw e;
    }
  },

  applyStyle: async (slideIds, style, sourceElements) => {
    const initial = get().currentPresentation;
    if (!initial) return;
    try {
      await api.applyStyle(slideIds, style, sourceElements);
      await get().fetchPresentation(initial.id, true);
      toast().show('success', `Estilo aplicado a ${slideIds.length} slide${slideIds.length !== 1 ? 's' : ''}`);
    } catch (e: any) {
      toast().show('error', 'No se pudo aplicar el estilo');
      throw e;
    }
  },

  addGroup: async (data) => {
    const { currentPresentation } = get();
    if (!currentPresentation) throw new Error('No presentation selected');
    try {
      const group = await api.createSlideGroup({ ...data, presentationId: currentPresentation.id });
      set({
        currentPresentation: {
          ...currentPresentation,
          groups: [...(currentPresentation.groups ?? []), group],
        },
      });
      return group;
    } catch (e: any) {
      toast().show('error', 'No se pudo crear el grupo');
      throw e;
    }
  },

  updateGroup: async (id, data) => {
    try {
      await api.updateSlideGroup(id, data);
      const { currentPresentation } = get();
      if (currentPresentation) {
        set({
          currentPresentation: {
            ...currentPresentation,
            groups: (currentPresentation.groups ?? []).map((g) => (g.id === id ? { ...g, ...data } : g)),
            slides: data.label
              ? currentPresentation.slides.map((s) => (s.groupId === id ? { ...s, groupLabel: data.label! } : s))
              : currentPresentation.slides,
          },
        });
      }
    } catch (e: any) {
      toast().show('error', 'No se pudo renombrar el grupo');
      throw e;
    }
  },

  deleteGroup: async (id) => {
    try {
      await api.deleteSlideGroup(id);
      const { currentPresentation } = get();
      if (currentPresentation) {
        set({
          currentPresentation: {
            ...currentPresentation,
            groups: (currentPresentation.groups ?? []).filter((g) => g.id !== id),
          },
        });
      }
    } catch (e: any) {
      toast().show('error', 'No se pudo eliminar el grupo');
      throw e;
    }
  },
}));
